/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { FunctionComponent, useEffect, useMemo, useState } from 'react';

import { DeviceModel } from '../../../../core/device';
import { BrandingUiInfoKeys } from '../../../../core/entities/BrandingUiInfoKeys';
import { ServicesClientImpl } from '../../../../core/interactors/ServicesClientImpl';
import { Locale } from '../../../../core/locale/Locale';
import { LocaleKeys } from '../../../../core/locale/LocaleKeys';
import { CursorManager, KeyManager } from '../../../../core/navigation';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import { BrandingUiUtility } from '../../../../core/utils/BrandingUiUtility';
import { userPrefVideoWindowSetSelectedOptionAction } from '../../../../store/settings/userpreferences/userprefvideowindow/userprefvideowindow.actions';
import {
    USER_PREF_VIDEO_WINDOW_BUTTON_ID,
    userPrefVideoWindowDisableGhostMode,
    userPrefVideoWindowEnableGhostMode,
} from '../../../../store/settings/userpreferences/userprefvideowindow/userprefvideowindow.reducers';
import { Mousetrap, ScreenPreview } from '../../../common';
import { ImageLoader } from '../../../ImageLoader';
import { ImageLoaderTypes } from '../../../ImageLoader/ImageLoaderTypes';
import SettingsVideoWindowInteractiveLayer from '../../InteractiveLayer/SettingsVideoWindowInteractiveLayer';
import { SettingsTemplate } from '../../SettingsTemplate';

import '../../SettingsCommon.scss';
import './UserPrefVideoWindowScreen.scss';

const UserPrefVideoWindowScreen: FunctionComponent = (): JSX.Element => {
    const dispatch = useTivoDispatch();
    // list of selectors
    const { isGhostModeActive, selectedOption } = useTivoSelector(
        (state: RootState) => state.userPrefVideoWindow,
    );
    const [isCursorActive, setIsCursorActive] = useState(false);
    // list of local state
    const [videoWindowOption, setVideoWindowOption] = useState('');
    // list of member variables
    const services = new ServicesClientImpl();
    const replaceValue = {
        MANUFACTURER_NAME: services.getBrandingUiInfoForKey(BrandingUiInfoKeys.MANUFACTURER_NAME),
    };
    const videoWindowPageDescription = BrandingUiUtility.formatDescription(
        Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW_PAGE_DESCRIPTION),
        replaceValue,
    );

    const onCursorStateChangeCallback = (state) => {
        setIsCursorActive(state);
        if (isCursorActive) dispatch(userPrefVideoWindowEnableGhostMode());
    };

    useEffect(() => {
        setIsCursorActive(DeviceModel.isWebOsPlatform() && CursorManager.isCursorAppeared());
        const isVideoWindowEnabled = DeviceModel.getAppSettings().isVideoWindowEnabled();
        const selectedVideoWindowOption = DeviceModel.getAppSettings().isVideoWindowEnabled()
            ? Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW_OPTION_YES)
            : Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW_OPTION_NO);
        setVideoWindowOption(selectedVideoWindowOption);

        if (isVideoWindowEnabled) dispatch(userPrefVideoWindowSetSelectedOptionAction(0));
        else dispatch(userPrefVideoWindowSetSelectedOptionAction(1));

        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        return () => {
            dispatch(userPrefVideoWindowDisableGhostMode());
        };
    }, []);

    useEffect(() => {
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);

        return () => {
            CursorManager.unsubscribe(refCursorManager);
        };
    }, []);

    useEffect(() => {
        if (selectedOption === 0) {
            setVideoWindowOption(Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW_OPTION_YES));
        } else {
            setVideoWindowOption(Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW_OPTION_NO));
        }
    }, [selectedOption]);

    const previewText = useMemo(() => {
        if (videoWindowOption === Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW_OPTION_YES)) {
            return Locale.getString(LocaleKeys.SETTINGS_HEADER_TITLE_VIDEO_WINDOW_ACTIVE_PREVIEW);
        }
        return Locale.getString(LocaleKeys.SETTINGS_HEADER_TITLE_VIDEO_WINDOW_INACTIVE_PREVIEW);
    }, [videoWindowOption]);

    return (
        <SettingsTemplate title={Locale.getString(LocaleKeys.SETTINGS_HEADER_TITLE_VIDEO_WINDOW)}>
            <div className="settings-common-content">
                <div className="settings-video-window-container">
                    <div className="description-text">{videoWindowPageDescription}</div>
                    {isCursorActive && <SettingsVideoWindowInteractiveLayer />}
                    <Mousetrap
                        className={`highlight${
                            isGhostModeActive ? ' ghost' : ''
                        } userpref-video-window-button`}
                        value={USER_PREF_VIDEO_WINDOW_BUTTON_ID}
                    >
                        <ImageLoader imageType={ImageLoaderTypes.LEFT_ARROW_ICON} />
                        <div className="select-option-name">{videoWindowOption}</div>
                        <ImageLoader imageType={ImageLoaderTypes.RIGHT_ARROW_ICON} />
                    </Mousetrap>
                </div>
            </div>
            <ScreenPreview>
                <div>{previewText}</div>
            </ScreenPreview>
        </SettingsTemplate>
    );
};

export default UserPrefVideoWindowScreen;
